import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { inr, num, dt, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';

// ── Pack one product's bulk stock into variant packs ───────────
const PackModal = ({ row, locationId, locations, defaultStoreId, onClose, onSaved }) => {
  const [units, setUnits] = useState({});   // variantId -> count
  const [toLoc, setToLoc] = useState(defaultStoreId || locationId || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (id, v) => setUnits((u) => ({ ...u, [id]: v }));
  const kgUsed = useMemo(() => (row.variants || []).reduce(
    (s, v) => s + ((Number(units[v.id]) || 0) * Number(v.weightGrams)) / 1000, 0), [units, row.variants]);
  const remaining = Number(row.available) - kgUsed;
  const valid = kgUsed > 0 && remaining >= -1e-9;

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const items = (row.variants || [])
        .filter((v) => Number(units[v.id]) > 0)
        .map((v) => ({ variantId: v.id, units: Number(units[v.id]) }));
      const { data } = await api.post('/packing', { productId: row.productId, locationId, toLocationId: Number(toLoc) || undefined, items });
      toast.success(`Packed — ${num(data.run.bulkQty)} kg used`); onSaved(); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Pack — ${row.product}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy || !valid}>{busy ? 'Packing…' : 'Pack now'}</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>Available to pack: <strong>{num(row.available)} {row.unit}</strong></p>
      <div className="field">
        <label>Send packed jars to</label>
        <select value={toLoc} onChange={(e) => setToLoc(e.target.value)}>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}{l.type === 'store' ? ' (store)' : ''}</option>)}
        </select>
      </div>
      {!row.variants?.length ? (
        <p className="form-error">This product has no pack sizes with a weight set. Add variants (250g/500g…) on the product first.</p>
      ) : (
        <>
          <table className="grid">
            <thead><tr><th>Pack size</th><th>Weight</th><th>MRP</th><th style={{ width: 120 }}>How many</th></tr></thead>
            <tbody>
              {row.variants.map((v) => (
                <tr key={v.id}>
                  <td>{v.weightLabel}</td>
                  <td className="muted">{v.weightGrams} g</td>
                  <td className="muted">{inr(v.sellingPrice)}</td>
                  <td><input type="number" min="0" step="1" value={units[v.id] || ''} onChange={(e) => set(v.id, e.target.value)} style={{ width: 90 }} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex mt" style={{ justifyContent: 'space-between' }}>
            <span>Used: <strong>{num(kgUsed)} kg</strong></span>
            <span className={remaining < -1e-9 ? 'neg' : 'muted'}>Remaining: {num(remaining)} kg</span>
          </div>
          {remaining < -1e-9 && <p className="form-error">Not enough stock to pack this many.</p>}
        </>
      )}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

// ── Split existing packed stock into a different pack size (repack) ──
const SplitModal = ({ row, locationId, onClose, onSaved }) => {
  const [targetId, setTargetId] = useState(row.targets?.[0]?.id || '');
  const [units, setUnits] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const target = (row.targets || []).find((t) => String(t.id) === String(targetId));
  const n = Number(units) || 0;
  const totalGrams = n * Number(row.weightGrams);
  const toUnits = target ? Math.floor(totalGrams / Number(target.weightGrams)) : 0;
  const remainderKg = target ? Math.round((totalGrams - toUnits * Number(target.weightGrams)) / 10) / 100 : 0;
  const overStock = n > Number(row.available) + 1e-9;
  const valid = n > 0 && target && toUnits >= 1 && !overStock;

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/packing/split', {
        productId: row.productId, locationId, fromVariantId: row.variantId, toVariantId: Number(targetId), units: n,
      });
      toast.success(`Split into ${data.split.toUnits} × ${target.weightLabel}`); onSaved(); onClose();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title={`Split ${row.product} — ${row.weightLabel}`} onClose={onClose}
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy || !valid}>{busy ? 'Splitting…' : 'Split now'}</button></>}>
      <p className="muted" style={{ marginTop: 0 }}>In stock: <strong>{num(row.available)} × {row.weightLabel}</strong> ({row.weightGrams} g each)</p>
      <div className="row">
        <div className="field"><label>How many {row.weightLabel} to split</label>
          <input type="number" min="1" step="1" max={row.available} value={units} onChange={(e) => setUnits(e.target.value)} autoFocus />
        </div>
        <div className="field"><label>Into pack size</label>
          <select value={targetId} onChange={(e) => setTargetId(e.target.value)}>
            {(row.targets || []).map((t) => <option key={t.id} value={t.id}>{t.weightLabel} ({t.weightGrams} g)</option>)}
          </select>
        </div>
      </div>
      {n > 0 && target && (
        <div className="card card-pad" style={{ background: 'var(--surface-2, #faf6ec)' }}>
          {toUnits >= 1
            ? <div><strong>{n} × {row.weightLabel}</strong> → <strong>{toUnits} × {target.weightLabel}</strong>{remainderKg > 0 && <span className="muted"> (+{remainderKg} kg back to stock)</span>}</div>
            : <span className="form-error">Too little weight to make even one {target.weightLabel} pack.</span>}
        </div>
      )}
      {overStock && <p className="form-error">Only {num(row.available)} × {row.weightLabel} in stock.</p>}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

const Packing = () => {
  const { can } = useAuth();
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState('');
  const [rows, setRows] = useState([]);
  const [packedRows, setPackedRows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packing, setPacking] = useState(null);
  const [splitting, setSplitting] = useState(null);
  const canWrite = can('packing:write');
  const storeId = (locations.find((l) => l.type === 'store') || {}).id;

  useEffect(() => {
    api.get('/locations').then(({ data }) => {
      const locs = data.locations || [];
      setLocations(locs);
      // Packing is done at the Warehouse (store) by default.
      if (!locationId && locs.length) setLocationId(String((locs.find((l) => l.type === 'store') || locs[0]).id));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => {
    if (!locationId) return;
    setLoading(true);
    Promise.all([
      api.get(`/packing/bulk?locationId=${locationId}`),
      api.get(`/packing/packed?locationId=${locationId}`),
      api.get('/packing'),
    ]).then(([b, p, r]) => { setRows(b.data.rows || []); setPackedRows(p.data.rows || []); setRuns(r.data.runs || []); })
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(load, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="section-head">
        <div />
        <select value={locationId} onChange={(e) => setLocationId(e.target.value)} style={{ width: 220 }}>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <p className="muted small" style={{ marginTop: '-.4rem' }}>Pack finished product into sellable jars. Defaults to the Warehouse — switch the location above to pack stock held elsewhere.</p>

      {loading ? <div className="spinner" /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
            {rows.map((row) => (
              <div className="make-card" key={row.productId}>
                <div className="make-card-title">{row.product}</div>
                <div className="make-card-meta"><span className="chip-static">{row.unit}</span><span><strong>{num(row.available)}</strong> {row.unit} available</span></div>
                <div className="make-card-sub muted">{(row.variants || []).map((v) => v.weightLabel).join(' · ') || 'No pack sizes set'}</div>
                {canWrite && row.variants?.length > 0 && (
                  <button className="btn btn-primary btn-block" style={{ marginTop: '.7rem' }} onClick={() => setPacking(row)}>Pack into jars</button>
                )}
              </div>
            ))}
            {!rows.length && <div className="kanban-empty">No stock to pack at this location. Make a batch in the kitchen first.</div>}
          </div>

          {packedRows.length > 0 && (
            <>
              <h3 style={{ marginTop: '1.6rem' }}>Split packed stock</h3>
              <p className="muted small" style={{ marginTop: '-.4rem' }}>Repack an existing pack size into another — e.g. split 500g packs into 250g. Total weight is conserved; any leftover returns to stock.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '1rem' }}>
                {packedRows.map((row) => (
                  <div className="make-card" key={`${row.productId}:${row.variantId}`}>
                    <div className="make-card-title">{row.product}</div>
                    <div className="make-card-meta"><span className="chip-static">{row.weightLabel}</span><span><strong>{num(row.available)}</strong> packs in stock</span></div>
                    <div className="make-card-sub muted">Split into: {row.targets.map((t) => t.weightLabel).join(' · ')}</div>
                    {canWrite && (
                      <button className="btn btn-outline btn-block" style={{ marginTop: '.7rem' }} onClick={() => setSplitting(row)}>Split / repack</button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <h3 style={{ marginTop: '1.6rem' }}>Recent packing</h3>
          <div className="table-wrap">
            <table className="grid">
              <thead><tr><th>Ref</th><th>Product</th><th>From</th><th>To (store)</th><th className="right">Used</th><th>Packed</th><th>When</th></tr></thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{r.code}</td>
                    <td>{r.product?.name}</td>
                    <td className="muted">{r.location?.name}</td>
                    <td>{r.toLocation?.name || r.location?.name}</td>
                    <td className="right">{num(r.bulkQty)} {r.bulkUnit}</td>
                    <td>{(r.items || []).map((it) => `${it.units} × ${it.variant?.weightLabel}`).join(', ')}</td>
                    <td className="muted">{dt(r.createdAt)}</td>
                  </tr>
                ))}
                {!runs.length && <tr><td colSpan={7} className="empty">No packing runs yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {packing && <PackModal row={packing} locationId={Number(locationId)} locations={locations} defaultStoreId={storeId} onClose={() => setPacking(null)} onSaved={load} />}
      {splitting && <SplitModal row={splitting} locationId={Number(locationId)} onClose={() => setSplitting(null)} onSaved={load} />}
    </div>
  );
};

export default Packing;
